import { memo } from "react";
import { Paper, Grid, Box, Chip } from "@mui/material";
import { Image, Button as ButtonAntd, Popconfirm, Spin } from "antd";
import { DeleteOutlined, CloudServerOutlined } from "@ant-design/icons";
import { ImagenComponent } from "./image-item";
import { UnifiedPhotoI } from "../../../hooks";
import { EquipmentAcPhotoI } from "../../../interfaces";

interface UnifiedPhotoSlotProps {
  photo: UnifiedPhotoI;
  onUpdate: (updated: UnifiedPhotoI, uuid: string) => void;
  onDeleteStorage: (storageId: number, uuid: string) => void;
  isDeleting?: boolean;
}

export const UnifiedPhotoSlot = memo(
  ({ photo, onUpdate, onDeleteStorage, isDeleting }: UnifiedPhotoSlotProps) => {
    const asLegacy: EquipmentAcPhotoI = {
      id: photo.id,
      uuid: photo.uuid,
      src: photo.src,
      observation: photo.observation,
      order: photo.order,
    };

    const handleLegacyUpdate = (updated: EquipmentAcPhotoI, uuid: string) => {
      onUpdate(
        {
          ...photo,
          src: updated.src,
          observation: updated.observation,
          isEmpty: !updated.src,
        },
        uuid,
      );
    };

    if (photo.isStorage && photo.storageId) {
      return (
        <Paper
          elevation={2}
          sx={{
            m: 0.5,
            p: 1,
            minWidth: "100%",
            height: 165,
            maxHeight: 165,
            minHeight: 165,
            position: "relative",
          }}
        >
          {/* Badge visual para distinguir fotos nuevas */}
          <Box sx={{ position: "absolute", top: 6, left: 6, zIndex: 1 }}>
            <Chip
              icon={<CloudServerOutlined style={{ fontSize: 10 }} />}
              label="Nueva"
              size="small"
              color="info"
              sx={{ fontSize: 10, height: 18 }}
            />
          </Box>

          <Grid container spacing={1} justifyContent="center">
            <Grid item xs={5}>
              <Spin spinning={isDeleting}>
                <Image
                  width="100%"
                  height={130}
                  src={photo.src}
                  style={{ borderRadius: 5, objectFit: "cover" }}
                />
              </Spin>
            </Grid>
            <Grid item xs={7}>
              <Paper variant="outlined" sx={{ p: 1, height: "100%" }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Popconfirm
                      title="¿Eliminar esta foto?"
                      description="Se eliminará permanentemente del servidor."
                      onConfirm={() =>
                        onDeleteStorage(photo.storageId!, photo.uuid)
                      }
                      okText="Sí, eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                    >
                      <ButtonAntd
                        type="dashed"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        loading={isDeleting}
                        disabled={isDeleting}
                      >
                        Eliminar
                      </ButtonAntd>
                    </Popconfirm>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        fontSize: 11,
                        color: "text.secondary",
                        mt: 1,
                        fontStyle: "italic",
                      }}
                    >
                      Foto subida al servidor
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      );
    }

    return <ImagenComponent data={asLegacy} onUpdate={handleLegacyUpdate} />;
  },
);

UnifiedPhotoSlot.displayName = "UnifiedPhotoSlot";
